import React from 'react';

/**
 * GoldenQuizWelcome - Welcome page for Golden Quiz (Quiz Master)
 *
 * Light color palette to distinguish from active learning environment
 * Explains capacity for recall, clarity, and cognitive honesty
 */
function GoldenQuizWelcome({ onBackToSIFISO, onEnterGoldenQuiz }) {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#F5F5F0',
      color: '#2A2A2A'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <header style={{ marginBottom: '50px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px'
          }}>
            <div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                color: '#D4AF37',
                marginBottom: '10px',
                letterSpacing: '-0.5px'
              }}>
                GOLDEN QUIZ
              </h1>
              <p style={{
                fontSize: '1.2rem',
                fontStyle: 'italic',
                color: '#6B6B6B'
              }}>
                Quiz Master
              </p>
            </div>
            <button
              onClick={onBackToSIFISO}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2A2A2A',
                color: '#F5F5F0',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3A3A3A'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
            >
              ← Return to SIFISO
            </button>
          </div>

          <div style={{
            height: '3px',
            background: 'linear-gradient(to right, #D4AF37, transparent)',
            marginBottom: '20px'
          }} />

          <p style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            color: '#2A2A2A',
            marginBottom: '15px'
          }}>
            Recall, Clarity, Confidence
          </p>
          <p style={{
            fontSize: '1rem',
            color: '#6B6B6B',
            lineHeight: '1.7',
            maxWidth: '700px'
          }}>
            The capacity to test your own understanding and surface hidden gaps. Cognitive honesty
            through active recall and demonstrated mastery.
          </p>
        </header>

        {/* Main Content */}
        <main>
          {/* Status Box */}
          <div style={{
            backgroundColor: '#FFFBF0',
            border: '2px solid #D4AF37',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '40px'
          }}>
            <div style={{
              fontSize: '0.85rem',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: '#D4AF37',
              marginBottom: '15px'
            }}>
              Now Active — 7 Categories Available
            </div>
            <p style={{
              fontSize: '1.05rem',
              color: '#2A2A2A',
              lineHeight: '1.7',
              marginBottom: '15px'
            }}>
              Golden Quiz is designed as a mirror, not a gate. It helps you see
              the difference between what you think you understand and what you actually understand.
            </p>
            <p style={{
              fontSize: '0.95rem',
              color: '#6B6B6B',
              lineHeight: '1.7',
              marginBottom: '25px'
            }}>
              This is not gamification. This is not point-scoring. This is cognitive honesty—
              the willingness to confront your own gaps and close them.
            </p>
            <button
              onClick={onEnterGoldenQuiz}
              style={{
                padding: '14px 32px',
                backgroundColor: '#D4AF37',
                color: '#2A2A2A',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#C4A030';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#D4AF37';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Enter Golden Quiz →
            </button>
          </div>

          {/* What This Builds */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What This Capacity Builds
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Active recall (retrieving knowledge from memory strengthens it)',
                  'Gap identification (seeing what you do not yet know)',
                  'Demonstrated mastery (proving understanding, not claiming it)',
                  'Mental models that persist under pressure',
                  'Confidence built on evidence, not self-assessment'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '15px' : '0',
                    fontSize: '1rem',
                    lineHeight: '1.6',
                    color: '#2A2A2A'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.3rem',
                      fontWeight: '700'
                    }}>
                      •
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Position in Loop */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              Position in the Loop
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '20px'
              }}>
                Golden Quiz runs throughout Golden Learn—not only at the end. It tests and
                strengthens all capacities as you build them.
              </p>
              <div style={{
                backgroundColor: '#F5F5F0',
                padding: '20px',
                borderRadius: '8px',
                borderLeft: '4px solid #D4AF37'
              }}>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#2A2A2A',
                  lineHeight: '1.7',
                  fontStyle: 'italic',
                  margin: 0
                }}>
                  "Golden Quiz is a mirror, not a gate. It shows you what you truly know.
                  When it reveals gaps, it links back to Golden Learn so you can consult
                  the library and close them."
                </p>
              </div>
            </div>
          </section>

          {/* What's Coming */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What's Being Designed
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '20px'
              }}>
                Golden Quiz will include:
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Micro quizzes after each Golden Learn level',
                  'Cumulative quizzes every 5 levels',
                  'Spaced repetition (returning to concepts over time)',
                  'Gap surfacing (clear indication of what needs review)',
                  'Deep links back to Golden Learn for consultation'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '12px' : '0',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    color: '#6B6B6B'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.1rem'
                    }}>
                      →
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Critical Distinction */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What Golden Quiz Is Not
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Not a score or ranking system',
                  'Not punitive (gaps are normal, expected)',
                  'Not a completion gate (it\'s diagnostic, not blocking)',
                  'Not gamified (no points, badges, or leaderboards)',
                  'Not about performance—about clarity'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '12px' : '0',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    color: '#6B6B6B'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.1rem'
                    }}>
                      ✗
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Principle */}
          <section>
            <div style={{
              backgroundColor: '#2A2A2A',
              color: '#F5F5F0',
              borderRadius: '10px',
              padding: '30px',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '1.2rem',
                fontWeight: '600',
                lineHeight: '1.7',
                margin: 0
              }}>
                You cannot apply what you cannot recall.
                <br />
                <span style={{ color: '#D4AF37', fontStyle: 'italic' }}>
                  You cannot teach what you do not truly understand.
                </span>
              </p>
            </div>
          </section>
        </main>

        {/* Footer Note */}
        <footer style={{
          marginTop: '60px',
          paddingTop: '30px',
          borderTop: '1px solid #E5E5D5',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '0.9rem',
            color: '#6B6B6B',
            lineHeight: '1.6'
          }}>
            Golden Quiz is open to everyone. Test your knowledge, learn from explanations,
            <br />
            and challenge yourself across multiple categories and difficulty levels.
          </p>
        </footer>
      </div>
    </div>
  );
}

export default GoldenQuizWelcome;
